// Every Configure operation that needs a backend, in one place. Wired to the real
// CHA-11134 API (`chargeback_back/consumer_clarity`), mounted under
// /api/consumer-clarity/configure/ — NOT the older /deflect/* surface in
// `DeflectPage/services.js`, which is a separate, still-unconnected playground
// layer for the rest of the page (enrollment status, lookups, preferences).
//
// The wizard imports these functions and nothing else; this module is the only
// file that translates between the wizard's local `buildingBlocks` shape and the
// backend's `profile` / `payment_identifiers` contract. The HTTP calls themselves
// live in ../api/services.js.

import { parsePhoneNumberFromString } from "libphonenumber-js"

import { getCachedCountries } from "../../../../Utils/phoneCountriesCache"
import { RECEIPT_CONFIGURE_SLUGS } from "../../constants/receipt-configure-slugs"
import SECTION_SCHEMA, { SECTION_KEYS } from "../../constants/steps"
import {
  fetchConfigure,
  postLogo,
  postProfileUpdate,
  postSaveDraft,
  postSubmit
} from "../api/services"
import { mapConfigureResponse } from "../utils/hydrate"
import { isNextEnabled } from "../utils/sectionValidation"

const CONTENT_SECTION_KEYS = SECTION_KEYS.filter(
  (k) => k !== RECEIPT_CONFIGURE_SLUGS.payment_identifiers && k !== RECEIPT_CONFIGURE_SLUGS.preview
)

// PhoneInput holds a country object ({code, number, countryCode, name, id}); the
// backend wants one string. Sending the object produced a 400 whose message read
// "This value does not match the required pattern" - DRF's RegexField rejects a
// non-string with its own overridden `invalid` message, so the wording blamed the
// regex for what was really a type mismatch.
const serializePhone = (value) => {
  if (!value) return ""
  if (typeof value === "string") return value.trim()
  const digits = String(value.number ?? "").replace(/\D/g, "")
  if (!digits) return ""
  const dialCode = String(value.code ?? "").replace(/\D/g, "")
  return dialCode ? `+${dialCode}${digits}` : digits
}

// The inverse, for prefill. `country` comes from libphonenumber-js rather than the
// countries list, which may not be cached yet at load time; QuestionField fills in
// the country's display name and id from the list once it is warm.
const parsePhone = (value) => {
  if (!value) return undefined
  if (typeof value === "object") return value
  const parsed = parsePhoneNumberFromString(String(value).trim())
  if (!parsed) return { number: String(value).replace(/\D/g, "") }
  return {
    code: String(parsed.countryCallingCode),
    countryCode: parsed.country,
    number: parsed.nationalNumber
  }
}

// Flatten the wizard's per-step buildingBlocks into the backend's flat `profile`
// object. `includeKeys`, when passed, restricts the fields to those sections
// (used by Save for later so an in-progress step's half-typed values — which
// the backend rejects on validation even though it wouldn't persist them —
// aren't wire-included).
const flattenProfile = (buildingBlocks, includeKeys = CONTENT_SECTION_KEYS) => {
  const keys = new Set(includeKeys)
  const profile = {}
  for (const key of CONTENT_SECTION_KEYS) {
    if (!keys.has(key)) continue
    const section = buildingBlocks?.[key] || {}
    for (const q of SECTION_SCHEMA[key] || []) {
      if (q.type === "upload") continue // logo goes through logo_id below
      profile[q.key] = q.type === "phone" ? serializePhone(section[q.key]) : (section[q.key] ?? "")
    }
  }
  applyLogoId(profile, buildingBlocks)
  applyCountryName(profile)
  return profile
}

// Backend keeps the display name in a separate `country` column
// (CharField(max_length=50)). The wizard only ever stores country_code (ISO2),
// so derive the name from the cached countries list at save time — the same
// list the CountrySelect picks from, so the two never disagree.
const applyCountryName = (profile) => {
  if (!("country_code" in profile)) return
  const code = String(profile.country_code || "").toUpperCase()
  if (!code) {
    profile.country = ""
    return
  }
  const match = (getCachedCountries() || []).find(
    (c) => (c.country_code || "").toUpperCase() === code
  )
  profile.country = match?.name || ""
}

// The logo has three states, and the backend reads all three from `logo_id`
// alone: a real id sets or replaces it, an explicit null removes it, and
// leaving the key out means unchanged. Sending null for "no id" would collapse
// the last two — which matters now that the backend can offer the onboarding
// logo with a null id (it has no attachment until a save keeps it), because
// the merchant would see a logo, save, and have it deleted.
const applyLogoId = (profile, buildingBlocks) => {
  const logo = buildingBlocks?.business_details?.business_logo

  if (logo === null) {
    profile.logo_id = null // Remove was pressed
    return
  }

  if (logo?.id) profile.logo_id = logo.id // uploaded, or already attached
  // otherwise leave the key out: unchanged
}

// Build Step 3's payload. Only sends real codes once the set is actually
// complete and valid (matches what the backend's submit-time validation
// requires); an in-progress set is sent as PRESERVE_EXISTING with no codes so
// Save for later never 400s while the merchant is still typing.
const buildPaymentIdentifiers = (buildingBlocks) => {
  const identifiers = buildingBlocks?.payment_identifiers || {}
  const billing_descriptors = identifiers.billing_descriptors || []
  const complete = isNextEnabled(
    RECEIPT_CONFIGURE_SLUGS.payment_identifiers,
    identifiers,
    buildingBlocks
  )

  if (!complete) {
    return {
      billing_descriptors,
      submission_mode: "PRESERVE_EXISTING",
      arn_codes: [],
      bin_codes: [],
      caid_codes: []
    }
  }

  if (identifiers.use_bin_caid) {
    return {
      billing_descriptors,
      submission_mode: "BIN_CAID",
      arn_codes: [],
      bin_codes: identifiers.bin_codes || [],
      caid_codes: identifiers.caid_codes || []
    }
  }

  return {
    billing_descriptors,
    submission_mode: "ARN",
    arn_codes: identifiers.arn_codes || [],
    bin_codes: [],
    caid_codes: []
  }
}

const buildRequestBody = (buildingBlocks) => ({
  profile: flattenProfile(buildingBlocks),
  payment_identifiers: buildPaymentIdentifiers(buildingBlocks)
})

// The longest ordered, gap-free prefix of steps that are fully answered — the
// only shape the backend's completed_steps accepts (no skipping ahead).
const computeCompletedSteps = (buildingBlocks) => {
  const steps = []
  for (const key of SECTION_KEYS) {
    if (key === RECEIPT_CONFIGURE_SLUGS.preview) break // preview has no fields of its own
    if (!isNextEnabled(key, buildingBlocks?.[key] || {}, buildingBlocks)) break
    steps.push(key)
  }
  return steps
}

// Field key -> the label the merchant actually sees, so a backend field error can
// name the input it belongs to instead of its wire name.
const FIELD_LABELS = Object.fromEntries(
  SECTION_KEYS.flatMap((key) => (SECTION_SCHEMA[key] || []).map((q) => [q.key, q.label]))
)

// Wrapper keys of the request body. They are nesting, not fields, so naming them in
// an error line ("Profile: ...") would tell the merchant nothing.
const ENVELOPE_KEYS = new Set(["profile", "payment_identifiers", "detail", "non_field_errors"])

// Every backend error - validation, auth, and the Receipt domain errors alike - comes
// back in the project-wide envelope built by core/error_handler.py:
//   {"message": {"detail": <string | {field: [msg]}>}, "status_code": N}
// `detail` holds either a sentence or a per-field map, so this walks whichever it is
// down to readable lines and labels each one with the input it belongs to.
const flattenErrors = (node, fieldKey) => {
  if (node === null || node === undefined) return []
  if (typeof node === "string") {
    const label = fieldKey && !ENVELOPE_KEYS.has(fieldKey) ? FIELD_LABELS[fieldKey] || fieldKey : ""
    return [label ? `${label}: ${node}` : node]
  }
  if (Array.isArray(node)) return node.flatMap((item) => flattenErrors(item, fieldKey))
  if (typeof node === "object") {
    return Object.entries(node).flatMap(([key, value]) => flattenErrors(value, key))
  }
  return [String(node)]
}

const errorDetail = (response) => {
  const data = response?.data
  const lines = flattenErrors(data?.message?.detail ?? data?.message ?? data).filter(Boolean)

  if (lines.length) return lines.join("\n")
  if (response?.problem === "NETWORK_ERROR" || response?.problem === "TIMEOUT_ERROR") {
    return "Could not reach the server. Check your connection and try again."
  }

  return "Something went wrong. Please try again."
}

// The single Configure read. Returns a complete normalised state, so the caller can
// replace its whole server-owned state with it even when every field is empty. A
// failure reports ok:false and still returns a complete state, so no caller is handed
// something that tempts it to keep what it had.
// `businessAccountId` selects the merchant whose profile is read; see
// fetchConfigure for how each role resolves it.
export const loadProfile = async (businessAccountId) => {
  const response = await fetchConfigure(businessAccountId)

  if (!response.ok) {
    return { ok: false, errors: errorDetail(response), state: mapConfigureResponse(null) }
  }

  const state = mapConfigureResponse(response.data)

  return {
    ok: true,
    errors: null,
    // The phone widget needs a country object; every other field is used verbatim.
    state: {
      ...state,
      content: { ...state.content, support_phone: parsePhone(state.content.support_phone) }
    }
  }
}

// "Save for later" — persists only the steps already committed via Next (§11.5).
// Also called on rail navigation. Payload is scoped to the same completed steps
// so an in-progress step's invalid draft (e.g. a half-typed email) doesn't 400
// the whole save: those fields wouldn't be persisted anyway.
export const saveDraft = async (buildingBlocks) => {
  const completed = computeCompletedSteps(buildingBlocks)
  const response = await postSaveDraft({
    completed_steps: completed,
    profile: flattenProfile(buildingBlocks, completed),
    payment_identifiers: buildPaymentIdentifiers(buildingBlocks)
  })

  if (!response.ok) return { ok: false, errors: errorDetail(response), state: null }

  // The save answers with the rebuilt state, so the caller can refresh which steps the
  // server now considers complete without a second request.
  return { ok: true, errors: null, state: mapConfigureResponse(response.data) }
}

// Record one step as completed. The backend has no separate "mark complete"
// call — completed_steps always travels with Save for later — so this is the
// same request.
export const markStepCompleted = async (buildingBlocks) => saveDraft(buildingBlocks)

// First submission (§11.13): receipt content is stored as the first version with no
// approval, while descriptors and codes become enrollment requests for support.
export const submitProfile = async (buildingBlocks) => {
  const response = await postSubmit(buildRequestBody(buildingBlocks))
  if (!response.ok) return { ok: false, errors: errorDetail(response) }
  return { ok: true }
}

// Subsequent editing (§11.13): content changes only — Step 3 is read-only by then —
// and each submit opens one Profile update record per Active program (§11.14).
export const submitProfileUpdate = async ({ buildingBlocks, baseVersionId }) => {
  const response = await postProfileUpdate({
    base_version_id: baseVersionId,
    profile: flattenProfile(buildingBlocks)
  })
  if (!response.ok) return { ok: false, errors: errorDetail(response) }
  return { ok: true }
}

// Logo upload. Returns an object-URL so the Step 2 preview works immediately, plus
// the real attachment id the next save/submit must send as profile.logo_id.
export const uploadLogo = async (file) => {
  if (!file) return { url: null, file_name: "", size: 0, id: null }

  const url = URL.createObjectURL(file)
  const formData = new FormData()
  formData.append("file", file)
  const response = await postLogo(formData)

  if (!response.ok) {
    return { url, file_name: file.name, size: file.size, id: null, error: errorDetail(response) }
  }

  return {
    url,
    file_name: response.data?.filename || file.name,
    size: response.data?.size ?? file.size,
    id: response.data?.id || null
  }
}
